/**
 * `GET /v1/timeline` (D06, §6.1) — o que aconteceu com um cliente ou com uma
 * oportunidade, em ordem cronológica.
 *
 * ── Referência, não conteúdo ───────────────────────────────────────────────
 *
 * Cada item traz `trecho` (curto, para a linha da tela) e `referencia` (os
 * ids de quem tem o dado completo). O texto inteiro da mensagem sai de
 * `read.messages`, pela tela composta (C24) — a timeline não o duplica,
 * porque duplicar conteúdo duplica PII com o prazo de retenção da fonte
 * (ADR-0009).
 *
 * ── `ocorrido_em`, não "projetado em" ──────────────────────────────────────
 *
 * A ordem é a do FATO. Evento que chegou atrasado por retry ou reconciliação
 * aparece no lugar dele na história, e não no fim — é o que faz duas leituras
 * da mesma timeline contarem a mesma história.
 *
 * ── Cinco tipos declarados, três com fonte hoje ────────────────────────────
 *
 * `mensagem`, `mudanca_de_etapa` e `tarefa` são alimentados. `nota` e
 * `evento_de_portal` existem no vocabulário e voltam vazios: o payload de
 * nota do Twenty não carrega o alvo (o vínculo é um webhook separado), e
 * conector de portal é a etapa E. Estão aqui para que a origem, quando
 * chegar, não mude o contrato de quem já consome.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';
import { InstanteSchema } from './valores.js';

/**
 * `encerramento` entrou em 25/09/2026, e com atraso.
 *
 * O worker grava a linha "Conversa encerrada" na timeline desde 01/09
 * (`apps/workers/src/conversa/encerramento.ts`), e o tipo nunca chegou aqui.
 * A tela valida a resposta pelo contrato, então todo cliente com uma conversa
 * encerrada tinha o 360 inteiro recusado — o cliente não abria.
 */
export const TipoDeItemDaTimelineSchema = z.enum([
  'mensagem',
  'mudanca_de_etapa',
  'tarefa',
  'nota',
  'evento_de_portal',
  'encerramento',
]);
export type TipoDeItemDaTimeline = z.infer<typeof TipoDeItemDaTimelineSchema>;

export const ItemDaTimelineSchema = z.object({
  tipo: TipoDeItemDaTimelineSchema,
  ocorrido_em: InstanteSchema,
  /** Presente quando o fato pertence a um cliente — o caso comum. */
  cliente_id: IdSchema.nullable(),
  /** Presente quando o fato pertence a uma oportunidade já cunhada (C13). */
  oportunidade_id: IdSchema.nullable(),
  /** Atalho da tela para abrir a conversa. Só em `mensagem`. */
  conversa_id: IdSchema.nullable(),
  /** Curto por CHECK no banco (280) — a linha da tela, não o conteúdo. */
  trecho: z.string().nullable(),
  /** Quem fez, quando dá para saber: atendente, membro do workspace, automação. */
  ator: z.string().nullable(),
  /**
   * Os ids de quem tem o dado completo, por tipo. Aberto de propósito: cada
   * tipo referencia o que faz sentido para ele, e fechar a forma aqui exigiria
   * mudar o contrato a cada origem nova.
   */
  referencia: z.record(z.string(), z.unknown()),
});

export type ItemDaTimeline = z.infer<typeof ItemDaTimelineSchema>;

export const PaginaDaTimelineSchema = z.object({
  itens: z.array(ItemDaTimelineSchema),
  pagina: z.object({
    cursor_proximo: z.string().nullable(),
    tem_mais: z.boolean(),
  }),
  frescor: z.object({
    stale: z.boolean(),
    projetado_em: InstanteSchema.nullable(),
  }),
});

export type PaginaDaTimeline = z.infer<typeof PaginaDaTimelineSchema>;
